import translations from "../lang/fr.json";
import { FormattedMessage, IntlProvider, MessageFormatElement, useIntl } from "react-intl";

import { FormatXMLElementFn, PrimitiveType } from "intl-messageformat";
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";

export type TranslationsId = keyof typeof translations;
type TranslationValuesRecord = Record<string, React.ReactNode | PrimitiveType | FormatXMLElementFn<React.ReactNode>>

interface VocabIntlContext {
  locale: string,

  getFormattedString: <V extends Record<string, any> = TranslationValuesRecord>(id: TranslationsId, values?: V) => string,

  APFormattedMessage<V extends Record<string, any> = TranslationValuesRecord>
  ({ id, values }: { id: TranslationsId, values?: V }): React.JSX.Element,

  getFormattedLevel: (level: number) => string,
}

export const useVocabIntl = () => useContext(VocabIntlContext);

const VocabIntlContext = createContext<VocabIntlContext>(null!);

interface VocabIntlProps {
  locale: string,
  messages?: Record<string, string> | Record<string, MessageFormatElement[]> | undefined
  children: ReactNode | ReactNode[]
  logError?: 0 | 1 | 2 | 3
}

function VocabIntl({ children }: { children: ReactNode | ReactNode[] }
): JSX.Element {
  const { formatMessage, locale } = useIntl();
  const VocabularyTranslations: VocabIntlContext = useMemo(() => {
    class VocabularyTranslations implements VocabIntlContext {
      locale: string = locale;

      public getFormattedString<V extends Record<string, any> = TranslationValuesRecord>(id: TranslationsId, values?: V): string {
        return formatMessage({
          id: id, defaultMessage: translations[id].defaultMessage,
        }, values)
      }

      public APFormattedMessage<V extends Record<string, any> = Record<string, TranslationValuesRecord>>
      ({ id, values }: { id: TranslationsId, values?: V }): React.JSX.Element {
        return <FormattedMessage id={id}
                                 defaultMessage={translations[id].defaultMessage}
                                 values={values}
        />
      }

      public getFormattedLevel(level: number): string {
        if (level < 7) {
          return this.getFormattedString("level-elementary", { level })
        } else if (level < 12) {
          return this.getFormattedString("level-secondary", { level })
        } else {
          return this.getFormattedString("level-post-secondary")
        }
      }
    }

    return new VocabularyTranslations();
  }, [locale]);

  return (
    <VocabIntlContext.Provider
      value={{
        locale: VocabularyTranslations.locale,
        getFormattedString: VocabularyTranslations.getFormattedString.bind(VocabularyTranslations),
        getFormattedLevel: VocabularyTranslations.getFormattedLevel.bind(VocabularyTranslations),
        APFormattedMessage: VocabularyTranslations.APFormattedMessage.bind(VocabularyTranslations),
      }}>
      {children}
    </VocabIntlContext.Provider>
  )
}

export function VocabIntlProvider({ locale, messages, logError = 0, children }: VocabIntlProps) {
  const [errors,] = useState<Error[]>([])
  useEffect(() => {
    if (logError > 0 && messages) {
      const timeout = setTimeout(() => {
        if (logError == 1) {
          const messageKeys = Object.keys(messages)
          Object.keys(translations).filter(key => {
            if (key in messageKeys)
              console.log("Missing Key:", key);
          })
        }
        if (logError == 2) {
          console.error(errors);
        }
      }, 5000)
      return () => {clearTimeout(timeout)}
    }

  }, [messages]);

  function onError(error: Error) {
    errors.push(error);
  }

  return (
    <IntlProvider locale={locale} messages={Object.assign(translations, messages)} onError={onError}>
      <VocabIntl>
        {children}
      </VocabIntl>
    </IntlProvider>
  )
}